import { Controller } from 'stimulus';
import $ from 'jquery';
import { isValidFile } from '@/js/modules/file_validation';

export default class extends Controller {
  static targets = ['row', 'list', 'form', 'file', 'upload'];
  static outlets = ['filter-dropdown'];
  static values = { flash: String };

  // Stimulus runs the row callbacks for the rendered rows before connect() and the filter outlet,
  // and again after disconnect(). Only rows added or removed in between change the count and filter.
  #connected = false;

  connect() {
    this.#connected = true;

    $(this.formTarget).fileupload({
      add: (_, data) => {
        if (isValidFile(data.files[0])) {
          data.submit();
        }
      },
      start: () => {
        this.#flash?.dispatchEvent(new CustomEvent('flash:hide'));
        this.uploadTarget.setAttribute('aria-busy', 'true');
      },
      done: (_, data) => this.listTarget.insertAdjacentHTML('beforeend', data.result),
      fail: (_, data) => {
        const message =
          data.jqXHR.responseJSON?.errors?.join(' ') || 'The file could not be uploaded.';
        this.#flash?.dispatchEvent(
          new CustomEvent('flash:show', { detail: { message, level: 'error' } })
        );
      },
      stop: () => this.uploadTarget.removeAttribute('aria-busy'),
    });
  }

  disconnect() {
    this.#connected = false;
    $(this.formTarget).fileupload('destroy');
  }

  browse() {
    this.fileTarget.click();
  }

  rowTargetDisconnected() {
    if (!this.#connected) {
      return;
    }

    this.#updateCount();

    const currentVendorNames = this.rowTargets.reduce(
      (acc, el) => acc.add(this.#getRowVendorName(el)),
      new Set()
    );

    this.#filterVendorNames.forEach((vendorName) => {
      !currentVendorNames.has(vendorName) && this.filterDropdownOutlet.remove(vendorName);
    });
  }

  rowTargetConnected(rowTarget) {
    // jquery-ujs fires ajax:success through jQuery only, once the row's delete link succeeds
    $(rowTarget).on('ajax:success', () => rowTarget.remove());

    if (!this.#connected) {
      return;
    }

    this.#updateCount();

    const possiblyNewVendor = this.#getRowVendorName(rowTarget);
    const vendorNames = this.#filterVendorNames;
    this.#filterRows(this.filterDropdownOutlet.activeValues, [rowTarget]);
    if (vendorNames.includes(possiblyNewVendor)) {
      return;
    }

    let beforeVendor = null;
    // "Unspecified" comes very last
    if (possiblyNewVendor !== 'Unspecified') {
      beforeVendor =
        vendorNames.find((vendorName) => vendorName.localeCompare(possiblyNewVendor) === 1) || null;
    }

    this.filterDropdownOutlet.add({ value: possiblyNewVendor }, beforeVendor);
  }

  filter(e) {
    const { detail } = e;
    const { filters } = detail;
    this.#filterRows(filters, this.rowTargets);
  }

  // The modal title and the order tracker's Attachments button both show the count
  #updateCount() {
    document.querySelectorAll(`[data-${this.identifier}-count]`).forEach((element) => {
      element.textContent = this.rowTargets.length;
    });
  }

  #filterRows(filters, rows) {
    const [filter] = filters;

    if (filter === '') {
      return rows.forEach((el) => el.classList.toggle('hide', false));
    }

    rows.forEach((el) => {
      el.classList.toggle('hide', this.#getRowVendorName(el) !== filter);
    });
  }

  #getRowVendorName(row) {
    return row.getAttribute(`data-${this.identifier}-vendor-name`);
  }

  get #flash() {
    return document.getElementById(this.flashValue);
  }

  get #filterVendorNames() {
    let vendorNames = this.filterDropdownOutlet.values;
    // we know first option is "protected" from modification and aren't a vendor
    vendorNames = vendorNames.slice(1);
    return vendorNames;
  }
}
